import { Button } from '@/components/ui/button';
import { CustomInput, Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { showToast } from '@/components/ui/sonner';
import { useMutation, useReactiveVar } from '@apollo/client';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { Appearance, loadStripe, StripePaymentElementOptions } from '@stripe/stripe-js';
import { CheckCircle, CheckCircle2, Gift, Info, LoaderCircle, User, UserCircle, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { authState } from '@/vars/auth';
import { Link } from 'react-router-dom';
import { getRoutes } from '@/routes/routePaths';
import GiftCardVisual from './GiftCardVisual';
import {
  COMPLETE_GIFT_CARD_ACTIVATION,
  CREATE_GIFT_CARD_ACTIVATION_PAYMENT_INTENT,
  UPDATE_GIFT_CARD_ACTIVATION_DETAILS_GIFT,
  UPDATE_GIFT_CARD_ACTIVATION_DETAILS_SELF,
} from '../Graphql/mutations';
import type { AccountPreview, ActivationState, PackageSummary } from '../index';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_KEY);

const stripeAppearance: Appearance = {
  theme: 'night',
  variables: {
    colorPrimary: '#F8B107',
    colorBackground: '#181A32',
    colorText: '#FFFFFF',
    colorTextSecondary: '#9CA3AF',
    borderRadius: '8px',
    fontSizeBase: '14px',
  },
};

type Tab = 'gift' | 'self';

interface DiscountInfo {
  promotionCode: string;
  percentOff?: number | null;
  originalAmount: number;
  discountAmount: number;
  finalAmount: number;
}

// ─── Shared promo code UI ──────────────────────────────────────────────────────

interface PromoCodeSectionProps {
  promoCode: string;
  setPromoCode: (v: string) => void;
  promoCodeError: string;
  appliedDiscount: DiscountInfo | null;
  isValidatingPromo: boolean;
  onApply: () => void;
  onRemove: () => void;
}

function PromoCodeSection({
  promoCode,
  setPromoCode,
  promoCodeError,
  appliedDiscount,
  isValidatingPromo,
  onApply,
  onRemove,
}: PromoCodeSectionProps) {
  return (
    <div className='rounded-2xl bg-[#181A32] p-5 flex flex-col gap-4'>
      <p className='text-sm font-semibold text-white'>Have a discount code?</p>

      {appliedDiscount ? (
        /* Applied — badge only; full breakdown shown in the payment section below */
        <div className='flex items-center justify-between bg-[#1A3D2E] p-3 rounded-xl'>
          <div className='flex items-center gap-2'>
            <CheckCircle className='w-5 h-5 text-[#4ADE80]' />
            <span className='text-sm font-medium text-[#4ADE80]'>
              {appliedDiscount.promotionCode} applied
              {appliedDiscount.percentOff != null ? ` (${appliedDiscount.percentOff}% off)` : ''}
            </span>
          </div>
          <button onClick={onRemove} className='text-[#9CA3AF] hover:text-white transition-colors'>
            <X className='w-4 h-4' />
          </button>
        </div>
      ) : (
        <div className='flex flex-col gap-2'>
          <div className='flex gap-2'>
            <Input
              id='promo-code-input'
              type='text'
              placeholder='Enter promo code'
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              className='flex-1 bg-[#0E1020] border-[#2A2D42] text-white placeholder:text-[#6B7280]'
              autoCorrect='off'
              autoCapitalize='characters'
              spellCheck={false}
            />
            <Button
              id='apply-promo-btn'
              type='button'
              variant='secondary'
              className='px-5'
              disabled={isValidatingPromo || !promoCode.trim()}
              onClick={onApply}
            >
              {isValidatingPromo ? <LoaderCircle className='h-4 w-4 animate-spin' /> : 'Apply'}
            </Button>
          </div>
          {promoCodeError && <p className='text-[#DC1D00] text-xs'>{promoCodeError}</p>}
        </div>
      )}
    </div>
  );
}

interface Props {
  code: string;
  activationSessionToken: string;
  packageSummary: PackageSummary;
  defaultTab?: 'gift' | 'self';
  onNext: (partial: Partial<ActivationState>) => void;
  onBack: () => void;
}

// ─── Section container ─────────────────────────────────────────────────────────

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className='rounded-2xl bg-[#181A32] p-5 flex flex-col gap-4'>
      <div className='flex flex-col gap-0.5'>
        <p className='text-sm font-semibold text-white'>{title}</p>
        {subtitle && <p className='text-xs text-[#9CA3AF]'>{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

// ─── Stripe checkout form ──────────────────────────────────────────────────────

interface CheckoutFormProps {
  amount: number;
  label: string;
  onBack: () => void;
  onNext: (partial: Partial<ActivationState>) => void;
  discount?: DiscountInfo | null;
}

function CheckoutForm({ amount, label, onBack, onNext, discount }: CheckoutFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [completeActivation] = useMutation(COMPLETE_GIFT_CARD_ACTIVATION);

  const paymentOptions: StripePaymentElementOptions = { layout: 'accordion' };

  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setIsLoading(true);
    try {
      await elements.submit();
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });
      if (error) {
        const msg =
          error.type === 'card_error' || error.type === 'validation_error'
            ? error.message
            : 'An unexpected error occurred.';
        showToast({ variant: 'error', message: msg ?? 'Payment failed.' });
        return;
      }
      if (paymentIntent?.status === 'succeeded') {
        const { data, errors: gqlErrors } = await completeActivation({
          variables: { paymentIntentId: paymentIntent.id },
        });
        if (gqlErrors?.length) throw new Error(gqlErrors[0].message);
        onNext({ completionStatus: data?.completeGiftCardActivationAfterPayment?.status });
      }
    } catch (err: any) {
      showToast({ variant: 'error', message: err?.message ?? 'Something went wrong.' });
    } finally {
      setIsLoading(false);
    }
  };

  const payAmount = discount?.finalAmount ?? amount;

  return (
    <form onSubmit={handlePay} className='flex flex-col gap-5'>
      <PaymentElement options={paymentOptions} />

      {/* Discount breakdown — shown when a promo is applied */}
      {discount && (
        <div className='flex flex-col gap-2 rounded-xl bg-[#0E1020] border border-[#2A2D42] p-3'>
          <div className='flex justify-between text-sm'>
            <span className='text-[#9CA3AF]'>Original Price</span>
            <span className='text-white'>${discount.originalAmount.toFixed(2)}</span>
          </div>
          <div className='flex justify-between text-sm'>
            <span className='text-[#4ADE80]'>
              Discount{discount.percentOff != null ? ` (${discount.percentOff}% off)` : ''}
            </span>
            <span className='text-[#4ADE80]'>-${discount.discountAmount.toFixed(2)}</span>
          </div>
          <div className='h-px bg-white/10' />
          <div className='flex justify-between text-sm font-medium'>
            <span className='text-white'>Final Price</span>
            <span className='text-[#E9CC7C] font-bold'>${discount.finalAmount.toFixed(2)}</span>
          </div>
        </div>
      )}

      {/* Buttons */}
      <div className='flex gap-3 pt-1'>
        <Button
          id='payment-back-btn'
          type='button'
          className='h-12 w-24 shrink-0 font-bold text-black border-0'
          style={{
            background:
              'linear-gradient(98deg, rgba(248,177,7,1) 0%, rgba(239,232,115,1) 100%)',
          }}
          onClick={onBack}
        >
          Back
        </Button>
        <Button
          id='gift-card-pay-btn'
          type='submit'
          className='flex-1 h-12 font-bold text-black whitespace-nowrap'
          style={{
            background:
              'linear-gradient(98deg, rgba(248,177,7,1) 0%, rgba(239,232,115,1) 100%)',
          }}
          disabled={isLoading || !stripe || !elements}
        >
          {isLoading ? (
            <LoaderCircle className='h-4 w-4 animate-spin' />
          ) : (
            `${label} $${payAmount.toFixed(2)}`
          )}
        </Button>
      </div>
    </form>
  );
}

// ─── Activate for Myself tab ───────────────────────────────────────────────────

interface SelfTabProps {
  activationSessionToken: string;
  packageSummary: PackageSummary;
  onBack: () => void;
  onNext: (partial: Partial<ActivationState>) => void;
}

function SelfTab({ activationSessionToken, packageSummary, onBack, onNext }: SelfTabProps) {
  const [phoneData, setPhoneData] = useState<{
    countryCode: string;
    nationalNumber: string;
  } | null>(null);
  const [phoneError, setPhoneError] = useState('');
  const [loading, setLoading] = useState(false);
  const [accountPreview, setAccountPreview] = useState<AccountPreview | null>(null);
  const [clientSecret, setClientSecret] = useState('');

  const { authenticated, userDetails } = useReactiveVar(authState);

  const [updateSelf] = useMutation(UPDATE_GIFT_CARD_ACTIVATION_DETAILS_SELF);
  const [createPaymentIntent] = useMutation(CREATE_GIFT_CARD_ACTIVATION_PAYMENT_INTENT);

  // ── Promo code state ──────────────────────────────────────────────────────────
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeError, setPromoCodeError] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState<DiscountInfo | null>(null);
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);

  const handleApplyPromo = async () => {
    setPromoCodeError('');
    setIsValidatingPromo(true);
    try {
      const { data: intentData, errors: intentErrors } = await createPaymentIntent({
        variables: { activationSessionToken, promotionCode: promoCode.trim() },
      });
      if (intentErrors?.length) {
        setPromoCodeError(intentErrors[0].message ?? 'Invalid promo code.');
        return;
      }
      const discount = intentData?.createGiftCardActivationPaymentIntent?.discount;
      if (!discount) {
        setPromoCodeError('Promo code could not be applied.');
        return;
      }
      setAppliedDiscount(discount as DiscountInfo);
      setClientSecret(intentData?.createGiftCardActivationPaymentIntent?.paymentIntentSecret ?? '');
    } catch (err: any) {
      setPromoCodeError(err?.message ?? 'Invalid promo code.');
    } finally {
      setIsValidatingPromo(false);
    }
  };

  const handleRemovePromo = async () => {
    setAppliedDiscount(null);
    setPromoCode('');
    setPromoCodeError('');
    try {
      const { data: intentData, errors: intentErrors } = await createPaymentIntent({
        variables: { activationSessionToken },
      });
      if (intentErrors?.length) throw new Error(intentErrors[0].message);
      setClientSecret(intentData?.createGiftCardActivationPaymentIntent?.paymentIntentSecret ?? '');
    } catch (err: any) {
      showToast({ variant: 'error', message: err?.message ?? 'Something went wrong.' });
    }
  };

  // Core verify logic — accepts params directly so it can be called from both
  // the button handler and the auto-verify effect without depending on state timing
  const doVerifyPhone = useCallback(async (cc: string, nat: string) => {
    setPhoneError('');
    setLoading(true);
    try {
      const { data, errors } = await updateSelf({
        variables: {
          activationSessionToken,
          activateForSelf: { countryCode: `+${cc}`, phoneNumber: nat },
        },
      });
      if (errors?.length) throw new Error(errors[0].message);

      const preview = data?.updateGiftCardActivationDetails?.accountPreview;
      if (!preview) {
        setPhoneError('No FitStatic account found for this number. Please try another.');
        return;
      }
      setAccountPreview(preview as AccountPreview);

      const { data: intentData, errors: intentErrors } = await createPaymentIntent({
        variables: { activationSessionToken },
      });
      if (intentErrors?.length) throw new Error(intentErrors[0].message);
      setClientSecret(
        intentData?.createGiftCardActivationPaymentIntent?.paymentIntentSecret ?? '',
      );
    } catch (err: any) {
      showToast({ variant: 'error', message: err?.message ?? 'Something went wrong.' });
    } finally {
      setLoading(false);
    }
  }, [activationSessionToken, updateSelf, createPaymentIntent]);

  // Button-triggered verify — reads from phone state
  const handleVerify = useCallback(() => {
    if (!phoneData?.countryCode || !phoneData?.nationalNumber) return;
    if (accountPreview) return;
    doVerifyPhone(phoneData.countryCode, phoneData.nationalNumber);
  }, [phoneData, accountPreview, doVerifyPhone]);

  // Auto-prefill + verify from the logged-in user's phone on mount
  // countryCode is on the User type but not yet in the generated GetCurrentUserQuery type —
  // cast to any until schema is regenerated.
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const details = userDetails as any;
    if (!authenticated || !details?.phoneNumber || !details?.countryCode) return;
    const cc = String(details.countryCode).replace('+', '');
    const nat = String(details.phoneNumber);
    setPhoneData({ countryCode: cc, nationalNumber: nat });
    doVerifyPhone(cc, nat);
  // Run once on mount — intentionally omitting deps to avoid re-triggering
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className='flex flex-col gap-4'>
      {/* Verify section */}
      <Section
        title='Verify Your FitStatic Account'
        subtitle="Enter the phone number linked to your FitStatic account. We'll look it up and verify it before proceeding to payment."
      >

        {/* Phone input */}
        <div className='relative'>
          <PhoneInput
            id='self-phone-input'
            defaultCountry='US'
            onChange={(value) => {
              setPhoneData({
                countryCode: value.countryCode,
                nationalNumber: value.nationalNumber,
              });
              if (phoneError) setPhoneError('');
              if (accountPreview) {
                setAccountPreview(null);
                setClientSecret('');
              }
            }}
            error={phoneError}
            disabled={!!accountPreview}
          />
          {/* Verified checkmark overlay */}
          {accountPreview && (
            <div className='absolute right-3 top-1/2 -translate-y-1/2'>
              <CheckCircle2 className='h-5 w-5 text-[#22c55e]' />
            </div>
          )}
        </div>

        {/* Verify button — shown until account is confirmed */}
        {!accountPreview && (
          <Button
            id='self-verify-btn'
            type='button'
            onClick={handleVerify}
            className='w-full h-11 font-bold text-black'
            style={{
              background:
                'linear-gradient(98deg, rgba(248,177,7,1) 0%, rgba(239,232,115,1) 100%)',
            }}
            disabled={loading || !phoneData?.nationalNumber}
            loading={loading}
          >
            {loading ? 'Looking up account…' : 'Verify Account'}
          </Button>
        )}

        {/* New-user callout — shown until an account is verified */}
        {!accountPreview && (
          <div className='flex items-start gap-3 rounded-xl bg-[#0A1628] border border-[#0A7AFF]/25 px-4 py-3'>
            <Info className='h-4 w-4 text-[#0A7AFF] mt-0.5 shrink-0' />
            <p className='text-xs text-[#A8ADB9] leading-relaxed'>
              <span className='text-white font-medium'>New to FitStatic?</span>{' '}
              You need a FitStatic account to receive the gift card balance.{' '}
              <Link
                to={getRoutes.Login}
                target='_blank'
                rel='noopener noreferrer'
                className='text-[#0A7AFF] font-medium underline-offset-2 hover:underline'
              >
                Create a free account
              </Link>{' '}
              first, then come back here to activate your gift card.
            </p>
          </div>
        )}

        {/* Account preview card */}
        {accountPreview && (
          <div className='flex items-center justify-between rounded-xl bg-[#0E1020] border border-[#2A2D42] p-4'>
            <div className='flex items-center gap-3'>
              {accountPreview.profileImage ? (
                <img
                  src={accountPreview.profileImage}
                  alt={accountPreview.displayName}
                  className='h-10 w-10 rounded-full object-cover'
                />
              ) : (
                <UserCircle className='h-10 w-10 text-[#6B7280]' />
              )}
              <div>
                <p className='font-semibold text-white text-sm'>
                  {accountPreview.displayName}
                </p>
                {accountPreview.phoneMasked && (
                  <div className='flex items-center gap-1 mt-0.5'>
                    <span className='text-xs text-[#9CA3AF]'>
                      📞 {accountPreview.phoneMasked}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className='flex items-center gap-1.5 rounded-full bg-[#052e16] border border-[#22c55e]/30 px-3 py-1'>
              <CheckCircle2 className='h-3.5 w-3.5 text-[#22c55e]' />
              <span className='text-[11px] font-semibold text-[#22c55e]'>
                Account Verified
              </span>
            </div>
          </div>
        )}

        {/* Info message */}
        {accountPreview && (
          <div className='flex items-start gap-2 rounded-xl bg-[#0E1020] border border-[#2A2D42] p-3'>
            <Info className='h-4 w-4 text-[#F8B107] mt-0.5 shrink-0' />
            <p className='text-xs text-[#9CA3AF]'>
              The gift card balance will be credited to{' '}
              <span className='text-white font-medium'>
                {accountPreview.displayName}&apos;s
              </span>{' '}
              FitStatic Account.
            </p>
          </div>
        )}

        {/* Change number link */}
        {accountPreview && (
          <button
            onClick={() => {
              setAccountPreview(null);
              setClientSecret('');
            }}
            className='text-xs text-[#9CA3AF] hover:text-white text-left transition-colors'
          >
            Not you?{' '}
            <span className='text-[#F8B107] hover:underline'>Use a different number</span>
          </button>
        )}
      </Section>

      {/* Promo code — shown once account is verified and payment intent is ready */}
      {clientSecret && (
        <PromoCodeSection
          promoCode={promoCode}
          setPromoCode={setPromoCode}
          promoCodeError={promoCodeError}
          appliedDiscount={appliedDiscount}
          isValidatingPromo={isValidatingPromo}
          onApply={handleApplyPromo}
          onRemove={handleRemovePromo}
        />
      )}

      {/* Payment Details — revealed after account verified */}
      {clientSecret && (
        <Section
          title='Payment Details'
          subtitle='Your payment is processed securely. Sessions will be credited to the verified account immediately after payment.'
        >
          <Elements
            key={clientSecret}
            stripe={stripePromise}
            options={{ clientSecret, appearance: stripeAppearance }}
          >
            <CheckoutForm
              amount={packageSummary.amount}
              label='Activate & Pay'
              onBack={onBack}
              onNext={(partial) =>
                onNext({ ...partial, flow: 'ACTIVATE_FOR_SELF', accountPreview })
              }
              discount={appliedDiscount}
            />
          </Elements>
        </Section>
      )}

      {/* Back button when payment not yet shown */}
      {!clientSecret && (
        <Button
          id='self-back-btn'
          type='button'
          className='w-full h-12 font-bold text-black border-0'
          style={{
            background:
              'linear-gradient(98deg, rgba(248,177,7,1) 0%, rgba(239,232,115,1) 100%)',
          }}
          onClick={onBack}
        >
          Back
        </Button>
      )}
    </div>
  );
}

// ─── Pay & Gift tab ────────────────────────────────────────────────────────────

interface GiftTabProps {
  activationSessionToken: string;
  packageSummary: PackageSummary;
  onBack: () => void;
  onNext: (partial: Partial<ActivationState>) => void;
}

function GiftTab({ activationSessionToken, packageSummary, onBack, onNext }: GiftTabProps) {
  const [values, setValues] = useState({
    purchaserName: '',
    purchaserEmail: '',
    recipientName: '',
    recipientEmail: '',
  });
  const [errors, setErrors] = useState<Partial<typeof values>>({});
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [lockedRecipient, setLockedRecipient] = useState<{ name: string; email: string } | null>(
    null,
  );

  const [updateGift] = useMutation(UPDATE_GIFT_CARD_ACTIVATION_DETAILS_GIFT);
  const [createPaymentIntent] = useMutation(CREATE_GIFT_CARD_ACTIVATION_PAYMENT_INTENT);

  // ── Promo code state ──────────────────────────────────────────────────────────
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeError, setPromoCodeError] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState<DiscountInfo | null>(null);
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);

  const handleApplyPromo = async () => {
    setPromoCodeError('');
    setIsValidatingPromo(true);
    try {
      const { data: intentData, errors: intentErrors } = await createPaymentIntent({
        variables: { activationSessionToken, promotionCode: promoCode.trim() },
      });
      if (intentErrors?.length) {
        setPromoCodeError(intentErrors[0].message ?? 'Invalid promo code.');
        return;
      }
      const discount = intentData?.createGiftCardActivationPaymentIntent?.discount;
      if (!discount) {
        setPromoCodeError('Promo code could not be applied.');
        return;
      }
      setAppliedDiscount(discount as DiscountInfo);
      setClientSecret(intentData?.createGiftCardActivationPaymentIntent?.paymentIntentSecret ?? '');
    } catch (err: any) {
      setPromoCodeError(err?.message ?? 'Invalid promo code.');
    } finally {
      setIsValidatingPromo(false);
    }
  };

  const handleRemovePromo = async () => {
    setAppliedDiscount(null);
    setPromoCode('');
    setPromoCodeError('');
    try {
      const { data: intentData, errors: intentErrors } = await createPaymentIntent({
        variables: { activationSessionToken },
      });
      if (intentErrors?.length) throw new Error(intentErrors[0].message);
      setClientSecret(intentData?.createGiftCardActivationPaymentIntent?.paymentIntentSecret ?? '');
    } catch (err: any) {
      showToast({ variant: 'error', message: err?.message ?? 'Something went wrong.' });
    }
  };

  const validate = () => {
    const errs: Partial<typeof values> = {};
    if (!values.purchaserName.trim()) errs.purchaserName = 'Required';
    if (!/\S+@\S+\.\S+/.test(values.purchaserEmail))
      errs.purchaserEmail = 'Valid email required';
    if (!values.recipientName.trim()) errs.recipientName = 'Required';
    if (!/\S+@\S+\.\S+/.test(values.recipientEmail))
      errs.recipientEmail = 'Valid email required';
    return errs;
  };

  const handleProceed = async () => {
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      const { errors: gqlErrors } = await updateGift({
        variables: {
          activationSessionToken,
          payAndGift: {
            purchaserName: values.purchaserName.trim(),
            purchaserEmail: values.purchaserEmail.trim(),
            recipientName: values.recipientName.trim(),
            recipientEmail: values.recipientEmail.trim(),
          },
        },
      });
      if (gqlErrors?.length) throw new Error(gqlErrors[0].message);

      const { data: intentData, errors: intentErrors } = await createPaymentIntent({
        variables: { activationSessionToken },
      });
      if (intentErrors?.length) throw new Error(intentErrors[0].message);
      setLockedRecipient({ name: values.recipientName.trim(), email: values.recipientEmail.trim() });
      setClientSecret(
        intentData?.createGiftCardActivationPaymentIntent?.paymentIntentSecret ?? '',
      );
    } catch (err: any) {
      showToast({ variant: 'error', message: err?.message ?? 'Something went wrong.' });
    } finally {
      setLoading(false);
    }
  };

  const set = (key: keyof typeof values) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setValues((v) => ({ ...v, [key]: e.target.value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: '' }));
    if (clientSecret) setClientSecret('');
  };

  const isLocked = !!clientSecret;

  return (
    <div className='flex flex-col gap-4'>
      {/* Your Information */}
      <Section
        title='Your Information'
        subtitle='A payment receipt will be sent to your email address.'
      >
        <div className='grid grid-cols-2 gap-3'>
          <CustomInput
            id='purchaser-name'
            label='Your Name*'
            value={values.purchaserName}
            onChange={set('purchaserName')}
            error={errors.purchaserName}
            disabled={isLocked}
          />
          <CustomInput
            id='purchaser-email'
            label='Your Email*'
            type='email'
            value={values.purchaserEmail}
            onChange={set('purchaserEmail')}
            error={errors.purchaserEmail}
            disabled={isLocked}
          />
        </div>
      </Section>

      {/* Recipient Information */}
      <Section
        title='Recipient Information'
        subtitle='The gift card and redemption instructions will be emailed to the recipient.'
      >
        <div className='grid grid-cols-2 gap-3'>
          <CustomInput
            id='recipient-name'
            label="Recipient's Name*"
            value={values.recipientName}
            onChange={set('recipientName')}
            error={errors.recipientName}
            disabled={isLocked}
          />
          <CustomInput
            id='recipient-email'
            label="Recipient's Email*"
            type='email'
            value={values.recipientEmail}
            onChange={set('recipientEmail')}
            error={errors.recipientEmail}
            disabled={isLocked}
          />
        </div>
      </Section>

      {/* No-account callout — only show before payment is initiated */}
      {!clientSecret && (
        <div className='flex items-start gap-3 rounded-xl bg-[#0A7AFF]/10 border border-[#0A7AFF]/25 px-4 py-3'>
          <Info className='h-4 w-4 text-[#0A7AFF] mt-0.5 shrink-0' />
          <p className='text-xs text-[#A8ADB9] leading-relaxed'>
            <span className='text-white font-medium'>Recipient doesn&apos;t have a FitStatic account yet?</span>
            {' '}No problem — the gift email will include a link to sign up. Their{' '}
            {packageSummary.sessionCount} session{packageSummary.sessionCount !== 1 ? 's' : ''} will
            be credited to their account as soon as they join.
          </p>
        </div>
      )}

      {/* Proceed button or Payment section */}
      {!clientSecret ? (
        <div className='flex gap-3'>
          <Button
            id='gift-back-btn'
            type='button'
            className='h-12 w-24 shrink-0 font-bold text-black border-0'
            style={{
              background:
                'linear-gradient(98deg, rgba(248,177,7,1) 0%, rgba(239,232,115,1) 100%)',
            }}
            onClick={onBack}
          >
            Back
          </Button>
          <Button
            id='gift-proceed-btn'
            className='flex-1 h-12 font-bold text-black whitespace-nowrap'
            style={{
              background:
                'linear-gradient(98deg, rgba(248,177,7,1) 0%, rgba(239,232,115,1) 100%)',
            }}
            onClick={handleProceed}
            disabled={loading}
            loading={loading}
          >
            {loading ? 'Processing…' : `Pay & Gift $${packageSummary.amount.toFixed(2)}`}
          </Button>
        </div>
      ) : (
        <>
          {isLocked && (
            <button
              onClick={() => {
              setClientSecret('');
              setLockedRecipient(null);
              setAppliedDiscount(null);
              setPromoCode('');
              setPromoCodeError('');
            }}
              className='text-xs text-[#9CA3AF] hover:text-white text-left'
            >
              Need to update recipient details?{' '}
              <span className='text-[#F8B107] hover:underline'>Edit</span>
            </button>
          )}

          <PromoCodeSection
            promoCode={promoCode}
            setPromoCode={setPromoCode}
            promoCodeError={promoCodeError}
            appliedDiscount={appliedDiscount}
            isValidatingPromo={isValidatingPromo}
            onApply={handleApplyPromo}
            onRemove={handleRemovePromo}
          />
          <Section
            title='Payment Details'
            subtitle='Your payment is processed securely via Stripe. The gift card will be dispatched to the recipient as soon as the payment clears.'
          >
            <Elements
              key={clientSecret}
              stripe={stripePromise}
              options={{ clientSecret, appearance: stripeAppearance }}
            >
              <CheckoutForm
                amount={packageSummary.amount}
                label='Pay & Gift'
                onBack={onBack}
                onNext={(partial) =>
                  onNext({
                    ...partial,
                    flow: 'PAY_AND_GIFT',
                    accountPreview: lockedRecipient
                      ? {
                          userId: '',
                          displayName: lockedRecipient.name,
                          profileImage: null,
                          phoneMasked: lockedRecipient.email,
                        }
                      : null,
                  })
                }
                discount={appliedDiscount}
              />
            </Elements>
          </Section>
        </>
      )}
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

export default function ActivationFormStep({
  code,
  activationSessionToken,
  packageSummary,
  defaultTab,
  onNext,
  onBack,
}: Props) {
  const [tab, setTab] = useState<Tab>(defaultTab ?? 'gift');

  return (
    // Responsive 2-column layout:
    //   mobile/tablet (<lg): single column — card above, form below
    //   desktop (≥lg):       card fixed left (sticky), form scrolls right
    <div className='grid grid-cols-1 lg:grid-cols-[480px_1fr] gap-8 lg:gap-12 lg:items-start'>

      {/* ── Left column: gift card visual + summary (sticky on desktop) ── */}
      <div className='lg:sticky lg:top-8 flex flex-col gap-4'>
        <GiftCardVisual
          price={`$${(packageSummary.originalPrice ?? packageSummary.amount).toFixed(2)}`}
          sessionCount={packageSummary.sessionCount}
          cardNumber={code}
        />

        {/* Card summary panel */}
        <div className='rounded-2xl overflow-hidden border border-white/10'>
          {/* Package name banner */}
          <div
            className='px-4 py-3 text-center'
            style={{ background: 'linear-gradient(98deg, rgba(248,177,7,1) 0%, rgba(239,232,115,1) 100%)' }}
          >
            <p className='text-sm font-bold text-black tracking-wide'>
              {packageSummary.planName}
            </p>
          </div>

          {/* Value + Sessions stats */}
          <div className='grid grid-cols-2 divide-x divide-white/10 bg-[#181A32]'>
            <div className='flex flex-col items-center gap-0.5 px-4 py-3'>
              <span className='text-[10px] uppercase tracking-widest text-[#9CA3AF] font-medium'>
                Face Value
              </span>
              <span
                className='text-xl font-bold'
                style={{
                  background: 'linear-gradient(90deg, #F8B107 0%, #EFE873 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                ${(packageSummary.originalPrice ?? packageSummary.amount).toFixed(2)}
              </span>
            </div>
            <div className='flex flex-col items-center gap-0.5 px-4 py-3'>
              <span className='text-[10px] uppercase tracking-widest text-[#9CA3AF] font-medium'>
                Sessions
              </span>
              <span
                className='text-xl font-bold'
                style={{
                  background: 'linear-gradient(90deg, #F8B107 0%, #EFE873 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {packageSummary.sessionCount}
              </span>
            </div>
          </div>

          {/* Card code */}
          <div className='flex flex-col items-center gap-1 px-4 py-3 bg-[#0E1020] border-t border-white/10'>
            <span className='text-[10px] uppercase tracking-widest text-[#9CA3AF] font-medium'>
              Gift Card Code
            </span>
            <span className='font-mono text-sm font-semibold text-white tracking-widest'>
              {code}
            </span>
          </div>
        </div>
      </div>

      {/* ── Right column: heading + tab selector + form ───────────────── */}
      <div className='flex flex-col gap-6'>

        {/* Payment Required heading */}
        <div className='text-center lg:text-left'>
          <h2 className='text-lg font-bold text-white'>Payment Required</h2>
          <p className='text-xs text-[#9CA3AF] mt-1'>
            This gift card has not been paid for yet. Choose an option below.
          </p>
        </div>

        {/* Tab selector cards */}
        <div className='grid grid-cols-2 gap-3'>
          {/* Pay & Gift */}
          <button
            onClick={() => setTab('gift')}
            className='flex flex-col gap-2 rounded-2xl p-4 text-left transition-colors'
            style={{
              background: '#181A32',
              border: `2px solid ${tab === 'gift' ? '#0A7AFF' : '#515764'}`,
            }}
          >
            <div className='flex items-center gap-2'>
              <Gift className='h-4 w-4 text-white' />
              <span className='text-sm font-semibold text-white'>Pay & Gift</span>
            </div>
            <p className='text-[11px] text-[#9CA3AF]'>For a friend or family member</p>
          </button>

          {/* Activate for Myself */}
          <button
            onClick={() => setTab('self')}
            className='flex flex-col gap-2 rounded-2xl p-4 text-left transition-colors'
            style={{
              background: '#181A32',
              border: `2px solid ${tab === 'self' ? '#0A7AFF' : '#515764'}`,
            }}
          >
            <div className='flex items-center gap-2'>
              <User className='h-4 w-4 text-white' />
              <span className='text-sm font-semibold text-white'>Activate for Myself</span>
            </div>
            <p className='text-[11px] text-[#9CA3AF]'>Add balance to my account</p>
          </button>
        </div>

        {/* Tab content — key resets state on tab switch */}
        {tab === 'gift' ? (
          <GiftTab
            key='gift'
            activationSessionToken={activationSessionToken}
            packageSummary={packageSummary}
            onBack={onBack}
            onNext={onNext}
          />
        ) : (
          <SelfTab
            key='self'
            activationSessionToken={activationSessionToken}
            packageSummary={packageSummary}
            onBack={onBack}
            onNext={onNext}
          />
        )}
      </div>
    </div>
  );
}
